import { Skip } from '@/src/utils/evaluateChain';
import { Chunk } from '@/src/core/streaming/chunk';
import { DicomFileDataLoader } from '@/src/core/streaming/dicom/dicomFileDataLoader';
import { DicomFileMetaLoader } from '@/src/core/streaming/dicom/dicomFileMetaLoader';
import { ReadDicomTagsFunction } from '@/src/core/streaming/dicom/dicomMetaLoader';
import { ImportHandler, asIntermediateResult } from '@/src/io/import/common';
import { getDicomDisplayOptions } from '@/src/io/import/dataSource';
import { getWorker } from '@/src/io/itk/worker';
import { FILE_EXT_TO_MIME } from '@/src/io/mimeTypes';
import { getErrorDetail } from '@/src/utils';
import { readDicomTags } from '@itk-wasm/dicom';
import {
  DICOM_FORCE_2D_STACK_ID_KEY,
  DICOM_FORCE_2D_STACK_INDEX_KEY,
  DICOM_FORCE_2D_STACK_KEY,
  DICOM_IGNORE_IMAGE_ORIENTATION_KEY,
} from '@/src/core/streaming/dicomDisplayOptions';

/**
 * Adds DICOM files to the extra context.
 * @param dataSource
 * @returns
 */
const handleDicomFile: ImportHandler = async (dataSource) => {
  if (
    dataSource.type !== 'file' ||
    dataSource.fileType !== FILE_EXT_TO_MIME.dcm
  ) {
    return Skip;
  }

  const readTags: ReadDicomTagsFunction = async (file) => {
    try {
      const result = await readDicomTags(file, { webWorker: getWorker() });
      return result.tags;
    } catch (error) {
      const detail = getErrorDetail(
        error,
        'the file could not be parsed as valid DICOM (check browser console for details)'
      );
      throw new Error(`Failed to read DICOM tags: ${detail}`, {
        cause: error,
      });
    }
  };

  const metaLoader = new DicomFileMetaLoader(dataSource.file, readTags);
  const dataLoader = new DicomFileDataLoader(dataSource.file);
  const chunk = new Chunk({
    metaLoader,
    dataLoader,
  });

  const dicomDisplayOptions = getDicomDisplayOptions(dataSource);
  if (dicomDisplayOptions?.ignoreImageOrientation) {
    chunk.setUserData(DICOM_IGNORE_IMAGE_ORIENTATION_KEY, true);
  }
  if (dicomDisplayOptions?.force2DStack) {
    chunk.setUserData(DICOM_FORCE_2D_STACK_KEY, true);
    chunk.setUserData(
      DICOM_FORCE_2D_STACK_ID_KEY,
      dicomDisplayOptions.force2DStackId
    );
    chunk.setUserData(
      DICOM_FORCE_2D_STACK_INDEX_KEY,
      dicomDisplayOptions.force2DStackIndex
    );
  }

  await chunk.loadMeta();

  return asIntermediateResult([
    {
      type: 'chunk',
      chunk,
      mime: FILE_EXT_TO_MIME.dcm,
      parent: dataSource,
    },
  ]);
};

export default handleDicomFile;
